import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { keys, markWhatsNewSeen } from "@/api/hooks";
import type { WhatsNew } from "@/api/types";
import { subscribeSeen } from "@/features/whatsNew/channel";
import { compareSemver, decide } from "@/features/whatsNew/logic";
import {
  hideDialog,
  isHandled,
  markHandled,
  openDialogVersion,
  showDialog,
  useWhatsNewDialog,
} from "@/features/whatsNew/state";
import {
  useAcknowledgeWhatsNew,
  useWhatsNewForTab,
  useWhatsNewGate,
} from "@/features/whatsNew/useWhatsNew";
import { WhatsNewDialog } from "@/features/whatsNew/WhatsNewDialog";

/** After a reload onto a new build, say what changed since the user's
 *  last visit: the post-reload half of one flow whose pre-reload half is
 *  the "Repoworks has been updated" banner (MaintenanceBanner). Mounted
 *  once, in AppShell, so pages outside it (login, welcome, invitations)
 *  never show it.
 *
 *  The query waits for its gate (``whatsNewGate``): never while the
 *  banner asks for a reload (this tab doesn't have the new features),
 *  during a deploy, while the backend is away or the browser offline,
 *  or in a hidden tab. Then ``decide`` either opens the dialog,
 *  acknowledges silently (a first visit, nothing new), or leaves the
 *  navbar dot (only fixes). Any close acknowledges the version the
 *  dialog opened for, and the other tabs close theirs. */
export function WhatsNewController(): React.JSX.Element | null {
  const qc = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const gateOpen = useWhatsNewGate();
  const query = useWhatsNewForTab({ enabled: gateOpen });
  const acknowledge = useAcknowledgeWhatsNew();
  const dialog = useWhatsNewDialog();
  // Where focus was when the dialog opened: on a fresh page load that is
  // the body, and closing should then land on the page's main region.
  const focusBodyOnOpen = React.useRef(false);

  const data = query.data;
  const pathname = location.pathname;
  React.useEffect(() => {
    // The gate applies to opening only: a deploy while the dialog is open
    // leaves it open (the banner shows behind it).
    if (!data || !gateOpen || isHandled(data.current_version)) return;
    const action = decide(data, pathname);
    if (action === "ack") {
      markHandled(data.current_version);
      acknowledge(data.current_version);
    } else if (action === "dialog") {
      markHandled(data.current_version);
      focusBodyOnOpen.current =
        typeof document !== "undefined" &&
        (document.activeElement === null || document.activeElement === document.body);
      showDialog({
        version: data.current_version,
        since: data.last_seen_version,
        notes: data.notes,
        omitted: data.omitted,
      });
    }
  }, [data, gateOpen, pathname, acknowledge]);

  React.useEffect(() => {
    if (query.error) console.warn("whats-new: release notes unavailable", query.error);
  }, [query.error]);

  // Another tab acknowledged: close ours without acknowledging again.
  React.useEffect(
    () =>
      subscribeSeen((seen) => {
        markHandled(seen);
        for (const [, cached] of qc.getQueriesData<WhatsNew>({ queryKey: keys.whatsNewAll })) {
          if (cached && compareSemver(cached.current_version, seen) <= 0) {
            markHandled(cached.current_version);
          }
        }
        markWhatsNewSeen(qc, seen);
        const open = openDialogVersion();
        if (open && compareSemver(open, seen) <= 0) hideDialog();
      }),
    [qc],
  );

  if (!dialog) return null;

  // A language switch refetches the same window in the new language;
  // show it in place (the version to acknowledge doesn't change).
  const live =
    data && data.current_version === dialog.version && data.notes.length > 0 ? data : null;

  const close = (then?: string) => {
    if (!dialog.open) return;
    hideDialog();
    acknowledge(dialog.version);
    if (then) navigate(then);
  };

  return (
    <WhatsNewDialog
      open={dialog.open}
      notes={live?.notes ?? dialog.notes}
      omitted={live?.omitted ?? dialog.omitted}
      since={dialog.since}
      onClose={close}
      onExited={() => {
        if (!focusBodyOnOpen.current) return;
        // Nothing had focus when it opened, so MUI has nothing to hand it
        // back to: focus stays on the button that closed it until that
        // unmounts, then drops to the body. Land on the page instead.
        const active = document.activeElement;
        const leftBehind =
          active === null ||
          active === document.body ||
          (active instanceof HTMLElement && active.closest('[role="dialog"]') !== null);
        if (leftBehind) document.querySelector<HTMLElement>("main")?.focus();
      }}
    />
  );
}
